import { Button, Checkbox, Input, Select, SelectItem, Textarea } from '@nextui-org/react';
import { Plus } from 'lucide-react';
import { X, Settings } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/constants';
import auth from '../../Auth';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../Breadcrumbs';

const NewPrompt = () => {
  const [prompt, setPrompt] = useState({
    prompt_id: '',
    max_tokens: null,
    reserved_tokens_budget_for_sampling: null,
    max_sample_tokens: null,
    __showSettings: false
  });
  const [chats, setChats] = useState([{ role: 'system' }]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const navigate = useNavigate();

  const addChat = () => {
    setChats([...chats, { role: 'user' }]);
  };

  const deleteChat = (chatIndex) => {
    setChats(chats.filter((_, i) => i !== chatIndex));
  };

  const updateChatSetting = (chatIndex, settingKey, newValue) => {
    const updatedChats = chats.map((chat, i) => {
      if (i === chatIndex) {
        if (settingKey === 'is_multiple') {
          if (newValue === false) {
            chat['while_fits'] = false;
            chat['continue_if_doesnt_fit'] = false;
            chat['add_in_reverse_order'] = false;
            chat['in_one_message'] = false;
          }
        }
        return { ...chat, [settingKey]: newValue };
      }
      return chat;
    });
    setChats(updatedChats);
  };

  const updatePromptSetting = (settingKey, newValue) => {
    setPrompt({ ...prompt, [settingKey]: newValue });
  };

  const savePrompt = async () => {
    var promptData = {
      prompt_id: prompt.prompt_id,
      temperature: prompt.temperature,
      max_tokens: prompt.max_tokens,
      reserved_tokens_budget_for_sampling: prompt.reserved_tokens_budget_for_sampling,
      max_sample_tokens: prompt.max_sample_tokens,
      chats: chats.map((chat) => {
        return {
          role: chat.role,
          content: chat.content,
          priority: parseInt(chat.priority || '0'),
          presentation: chat.presentation,
          last_words: chat.last_words,
          is_multiple: chat.is_multiple,
          while_fits: chat.while_fits,
          continue_if_doesnt_fit: chat.continue_if_doesnt_fit,
          add_in_reverse_order: chat.add_in_reverse_order,
          in_one_message: chat.in_one_message,
          label: chat.label,
          add_if_fitted_labels: chat.add_if_fitted_labels,
          required: chat.required
        };
      })
    };
    var errors = [];
    if (promptData.prompt_id === '') {
      errors.push('Prompt id is required');
    }
    if (promptData.chats.length === 0) {
      errors.push('At least one chat is required');
    }
    if (promptData.chats.filter((chat) => !chat.content).length > 0) {
      errors.push('All chats must have content');
    }
    if (promptData.temperature && (promptData.temperature < 0 || promptData.temperature > 1)) {
      errors.push('Temperature must be between 0 and 1');
    }
    if (promptData.max_tokens && promptData.max_tokens < 1) {
      errors.push('Max tokens must be greater than 0');
    }
    if (promptData.reserved_tokens_budget_for_sampling && promptData.reserved_tokens_budget_for_sampling < 1) {
      errors.push('Reserved tokens budget for sampling must be greater than 0');
    }
    if (promptData.max_sample_tokens && promptData.max_sample_tokens < 1) {
      errors.push('Max sample tokens must be greater than 0');
    }
    if (errors.length > 0) {
      console.error('Errors:', errors);
      setAlertMessage(errors.join('; ') + '. Please fix the errors and try again.');
      setShowAlert(true);
      return;
    }

    console.log(promptData);
    // send PUT request to save promptData PUT {API_URL}/prompts
    const response = await axios.put(`${API_URL}/prompts`, promptData, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    if (response.status === 200) {
      console.log('Prompt saved successfully:', response.data);
      navigate(`/prompts/${promptData.prompt_id}`);
    } else {
      // TODO review all error codes
      console.error('Error saving prompt:', response.data.error);
    }
  };

  const toggleSettings = (chatIndex) => {
    const updatedChats = chats.map((chat, i) => {
      if (i === chatIndex) {
        return { ...chat, __showSettings: !chat.__showSettings };
      }
      return chat;
    });
    setChats(updatedChats);
  };
  function renderSettingsPromptInput(settingKey, label, type = 'text', defaultValue = '', placeholder = '') {
    return (
      <Input
        clearable
        bordered
        label={label}
        placeholder={placeholder || label}
        value={prompt[settingKey] ?? defaultValue}
        onChange={(e) => updatePromptSetting(settingKey, e.target.value)}
        type={type}
      />
    );
  }
  function renderSettingsInput(chat, i, settingKey, label, type = 'text', defaultValue = '', placeholder = '') {
    return (
      <Input
        clearable
        bordered
        label={label}
        placeholder={placeholder || label}
        value={chat[settingKey] ?? defaultValue}
        onChange={(e) => updateChatSetting(i, settingKey, e.target.value)}
        type={type}
      />
    );
  }
  function renderSettingsSelect(chat, i, settingKey, label, options, defaultValue) {
    return (
      <Select
        clearable
        bordered
        label={label}
        placeholder={label}
        value={chat[settingKey] ?? defaultValue}
        defaultSelectedKeys={[chat[settingKey] ?? defaultValue]}
        onChange={(e) => updateChatSetting(i, settingKey, e.target.value)}
      >
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </SelectItem>
        ))}
      </Select>
    );
  }
  function renderSettingsCheckbox(chat, i, settingKey, label) {
    return (
      <Checkbox isSelected={chat[settingKey] ?? false} onChange={(e) => updateChatSetting(i, settingKey, e.target.checked)}>
        {label}
      </Checkbox>
    );
  }
  const renderLine = () => {
    return <div style={{ width: '100%', height: '1px', backgroundColor: 'black', margin: '10px 0' }}></div>;
  };
  const divStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)', // creates 4 columns of equal width
    gap: '10px', // space between columns and rows
    marginTop: '20px'
  };

  const placeholder_textarea_first = 'Chat content here. Please, use {} to replace data from content, like: hello {username}!';

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/prompts', breadcrumb: 'Prompts' },
    { path: window.location.href, breadcrumb: 'Create New Prompt' }
  ];
  return (
    <div className='grid grid-cols-3 gap-4 p-4'>
      <Breadcrumbs crumbs={crumbs} />
      <h1 className='col-span-4 text-2xl font-bold'>Create New Prompt</h1>
      {showAlert && (
        <div className='col-span-3 overflow-auto'>
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative' role='alert'>
            <strong className='font-bold'>Error! </strong>
            <span className='block sm:inline'>{alertMessage}</span>
            <span className='absolute top-0 bottom-0 right-0 px-4 py-3' onClick={() => setShowAlert(false)}>
              <X className='fill-current h-6 w-6 text-red-500' size={18} />
            </span>
          </div>
        </div>
      )}

      <div className='col-span-3 overflow-auto'>
        <div className='flex gap-4'>
          <Input
            value={prompt.prompt_id}
            onChange={(e) => updatePromptSetting('prompt_id', e.target.value)}
            className='w-80'
            label='Prompt Id'
          />
          <Settings
            onClick={() => updatePromptSetting('__showSettings', !prompt.__showSettings)}
            style={{ marginTop: 12, cursor: 'pointer' }}
            size={30}
          ></Settings>
        </div>
        {prompt.__showSettings && (
          <div style={divStyle}>
            <h2 className='col-span-4 text-xl font-bold'>Additional settings:</h2>
            {renderSettingsPromptInput('max_tokens', 'Max tokens on the prompt', 'number', '', '4000')}
            {renderSettingsPromptInput('temperature', 'Temperature of the prompt', 'float', 0.0)}
            {renderSettingsPromptInput('max_sample_tokens', 'Max budget that can be used for sampling', 'number', '', '2000')}
            {renderSettingsPromptInput(
              'reserved_tokens_budget_for_sampling',
              'Budget that will be reserved for the answer',
              'number',
              '',
              '1200'
            )}
          </div>
        )}
      </div>
      <div className='col-span-3 overflow-auto'>
        <h2 className='col-span-4 text-xl font-bold'>Your chats with AI-agents:</h2>
        {chats.map((chat, i) => (
            <div key={i} className='mb-5'>
              <div className='flex gap-4'>
                <Textarea
                    className='chat-input-area'
                    placeholder={i === 0 ? placeholder_textarea_first : ''}
                    resizable
                    onChange={(e) => updateChatSetting(i, 'content', e.target.value)}
                />
                <div>
                  <X onClick={() => deleteChat(i)} style={{cursor: 'pointer'}} size={30}/>
                  <Settings onClick={() => toggleSettings(i)} style={{marginTop: 12, cursor: 'pointer'}}
                            size={30}></Settings>
                </div>
              </div>
              {chat.__showSettings && (
                  <div>
                    <div style={divStyle}>
                      {renderSettingsSelect(chat, i, 'role', 'Role', ['user', 'system', 'assistant'], 'user')}
                      {renderSettingsInput(chat, i, 'priority', 'Priority', 'number', 0)}
                      {renderSettingsInput(chat, i, 'presentation', 'Starting content with', 'text', '', 'Ex. Open files are:')}
                      {renderSettingsInput(chat, i, 'last_words', 'Ending content with', 'text', '', 'Ex.: !')}
                    </div>
                    {renderLine()}
                    <div style={divStyle}>
                      {renderSettingsCheckbox(chat, i, 'is_multiple', 'Add as multiple objects')}
                      {/* The following checkboxes will be displayed only if is_multiple is true */}
                      {chat['is_multiple'] && (
                          <>
                            {renderSettingsCheckbox(chat, i, 'while_fits', 'add objects while fit', !chat['is_multiple'])}
                            {renderSettingsCheckbox(
                                chat,
                                i,
                                'continue_if_doesnt_fit',
                                "continue if one object doesn't fit in prompt",
                                !chat['is_multiple']
                            )}
                            {renderSettingsCheckbox(chat, i, 'add_in_reverse_order', 'add items in reverse order', !chat['is_multiple'])}
                            {renderSettingsCheckbox(chat, i, 'in_one_message', 'send items in one message', !chat['is_multiple'])}
                          </>
                      )}
                    </div>
                    {renderLine()}
                    <div style={divStyle}>
                      {renderSettingsInput(chat, i, 'label', 'Chat label', 'text', '', 'Ex. system_msg')}
                      {renderSettingsInput(
                          chat,
                          i,
                          'add_if_fitted_labels',
                          'Add if fully fitted chat-labels (through comma)',
                          'text',
                          '',
                          'Ex. system_msg,user_question'
                      )}
                      {renderSettingsCheckbox(chat, i, 'required', 'Required')}
                    </div>
                    {renderLine()}
                  </div>
              )}
            </div>
        ))}
        <div className='flex '>
          <Button color='primary' onClick={() => addChat()}>
            <Plus className='h-4 w-4'/>
          </Button>
        </div>
        <div className='flex justify-end'>
          <Button color='success' className='mt-auto' onClick={() => savePrompt()}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
export default NewPrompt;
